import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AuthStore } from '../auth/auth.store';
import { MagicLinkResponse } from '../models/coverage-validator-api-models';
import { MagicLinkService } from './magic-link.service';

interface MagicLinkState {
  linkId: string | null;
  link: MagicLinkResponse | null;
  codeSent: boolean;
  error: string | null;
}

const initialState: MagicLinkState = {
  linkId: null,
  link: null,
  codeSent: false,
  error: null,
};

function errorMessage(e: unknown): string {
  switch (e instanceof HttpErrorResponse ? e.status : 0) {
    case 401:
      return "That code didn't work. Check it and try again, or send a new code.";
    case 404:
      return "This link isn't valid. Use the button in your most recent Coverage Validator email.";
    case 410:
      return 'This link has expired. Please contact your agent.';
    case 429:
      return 'A code was just sent. Wait a minute before asking for another.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export const MagicLinkStore = signalStore(
  { providedIn: 'root' },
  withState<MagicLinkState>(initialState),
  withMethods((store, magicLinkService = inject(MagicLinkService), authStore = inject(AuthStore)) => ({
    // Resolves true when a saved token is already accepted for this link, so the code step is skipped.
    async openLink(linkId: string): Promise<boolean> {
      patchState(store, { ...initialState, linkId });
      try {
        const link = await magicLinkService.getLink(linkId);
        patchState(store, { link });
        return !!link.isVerified;
      } catch (e) {
        patchState(store, { error: errorMessage(e) });
        return false;
      }
    },

    async sendCode() {
      patchState(store, { error: null });
      try {
        await magicLinkService.sendCode(store.linkId()!);
        patchState(store, { codeSent: true });
      } catch (e) {
        patchState(store, { error: errorMessage(e) });
      }
    },

    async verify(code: string): Promise<boolean> {
      patchState(store, { error: null });
      try {
        const response = await magicLinkService.verifyCode(store.linkId()!, code);
        authStore.setToken(response.access_token!);
        return true;
      } catch (e) {
        patchState(store, { error: errorMessage(e) });
        return false;
      }
    },
  })),
);
